import { format, parseISO } from 'date-fns';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../../network/ApiCall';
import ReactDataGrid from '@inovua/reactdatagrid-community';
import '@inovua/reactdatagrid-community/index.css'
import { PUBLIC_APP_URL } from '../../config.js';
import DateFilter from '@inovua/reactdatagrid-community/DateFilter'
import { getReasonPhrase } from 'http-status-codes';

const JsonApiLogGrid = () => {

  const { t } = useTranslation();

  const [gridRef, setGridRef] = useState(null);
  const gridStyle = { minHeight: 550 };
  const SEPARATOR = ',';

  const getData = async ({ skip, limit, sortInfo, filterValue }) => {
    const sortData = sortInfo ?? null;
    const url = "/jsonApiLog"
    const result = await axiosInstance.get(url, {
      params: {
        start: skip,
        length: limit,
        filter: encodeURIComponent(JSON.stringify(filterValue)),
        sort: encodeURIComponent(JSON.stringify(sortData))
      }
    })

    const data = result.data.data;
    const total = result.data.recordsTotal;

    return { data, count: parseInt(total) }
  };

  const downloadBlob = (blob, fileName = t('jsonApiLog:lblExportFileName') + '.csv') => {
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', fileName);
    link.style.position = 'absolute';
    link.style.visibility = 'hidden';

    document.body.appendChild(link);

    link.click();

    document.body.removeChild(link);
  };

  const exportCSV = () => {

    const columns = gridRef.current.visibleColumns.filter((c) => c.export !== false);
    const header = columns.map((c) => c.name).join(SEPARATOR);
    const rows = gridRef.current.data.map((data) => {
      return columns.map((c) => {
        if (c.name === 'URL' && data.url)
          return PUBLIC_APP_URL + "/app" + data.url
        // Apply formatter if it exists
        if (c.render) {
          return c.render({ data });
        }
        // Otherwise return raw data
        return data[c.id];
      }).join(SEPARATOR)
    }
    );

    const contents = [header].concat(rows).join('\n');
    const blob = new Blob([contents], { type: 'text/csv;charset=utf-8;' });

    downloadBlob(blob);
  };


  const gridI18n = Object.assign({}, ReactDataGrid.defaultProps.i18n, {
    "calendar.todayButtonText": t("common:lblToday")
  })


  const loadJson = (type, data) => {

    const url = `/jsonApiLog/${type}/${data.id}`;
    const filename = 'jsonApiLog-' + data.id + '.json';

    axiosInstance.get(url, {
      responseType: 'blob',
    })
      .then(response => {
        const blob = new Blob([response.data], { type: 'application/json', filename: filename });
        const url = window.URL.createObjectURL(blob);
        window.open(url)
      }).catch(error => {
        console.error(error)
      });
  }

  const columns = useMemo(() => [
    {
      header: t('jsonApiLog:lblDateCreated'),
      name: 'dateCreated',
      sortable: true,
      enableColumnFilterContextMenu: false,
      filterEditor: DateFilter,
      filterEditorProps: (props, { index }) => {
        // for range and notinrange operators, the index is 1 for the after field
        return {
          dateFormat: 'DD/MM/YYYY',
          cancelButton: false,
          highlightWeekends: false,

        }
      },
      render: ({ data }) => format(parseISO(data.dateCreated, 'yyyy-MM-d'), 'd/MM/yyyy HH:mm')
    },
    {
      header: t('jsonApiLog:lblKey'),
      name: 'apiKey',
      sortable: true,
      flex: 1
    },
    {
      header: t('jsonApiLog:lblClientIP'),
      name: 'clientIp',
      sortable: true
    }
    , {
      header: t('jsonApiLog:lblEndpoint'),
      name: 'endpoint',
      sortable: true,
      flex: 1,
    },
    {
      header: t('jsonApiLog:lblResponseStatus'),
      name: 'responseStatus',
      sortable: true,
      render: ({ data }) => data.responseStatus && (data.responseStatus + " (" + getReasonPhrase(data.responseStatus) + ")")
    },
    {
      header: t('jsonApiLog:lblRequest'),
      name: 'request',
      sortable: true,
      export: false,
      render: ({ data }) => <button className="btn btn-link" onClick={() => loadJson('getRequest', data)}>{t('jsonApiLog:btnViewRequest')}</button>
    },
    {
      header: t('jsonApiLog:lblResponse'),
      name: 'response',
      sortable: true,
      export: false,
      render: ({ data }) => <button className="btn btn-link" onClick={() => loadJson('getResponse', data)}>{t('jsonApiLog:btnViewResponse')}</button>
    },
  ], [t]);

  const filterValue = [
    { name: 'dateCreated', operator: 'eq', type: 'date', value: '' },
    { name: 'apiKey', operator: 'eq', type: 'string', value: '' },
    { name: 'clientIp', operator: 'contains', type: 'string', value: '' },
    { name: 'endpoint', operator: 'contains', type: 'string', value: '' }
  ];


  return (
    <div>
      <div className="table-responsive">

        <ReactDataGrid
          i18n={gridI18n}
          handle={setGridRef}
          style={gridStyle}
          columns={columns}
          dataSource={getData}
          defaultSortInfo={{ dir: 0, id: 'dateCreated', name: 'dateCreated' }}
          pagination
          defaultFilterValue={filterValue}
          showColumnMenuTool={false}
          defaultLimit={20}        
          pageSizes={[20,50,100,200,400]}  
        />

        <button className='mt-5 btn btn-outline-primary' onClick={exportCSV}>
          {t('jsonApiLog:btnExport')}
        </button>
      </div>

    </div>
  );
};

export default JsonApiLogGrid;